import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { MdOutlineCancel } from 'react-icons/md';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { useStateContext } from '../contexts/ContextProvider';
import { MenuItem } from '../types';
import TMLogo from './TMLogo';
import TMLogoIcon from './TMLogoIcon';
import { routeIconMap } from '../config/iconConfig';


const Sidebar: React.FC = () => {
  const {
    sidebarState,
    setSidebarState,
    screenSize,
    isAdminUser,
  } = useStateContext();

  const handleCloseSideBar = () => {
    if (sidebarState !== 'hidden' && screenSize && screenSize <= 900) {
      setSidebarState('hidden');
    }
  };

  const multiLevelLinks = [
    {
      title: '',
      links: [
        {
          name: 'Dashboards',
          icon: routeIconMap['dashboards'].icon,
          children: [
            {
              name: 'Clinician',
              icon: routeIconMap['clinician'].icon,
              path: 'dashboards/clinician'
            },
            {
              name: 'Billing',
              icon: routeIconMap['billing'].icon,
              path: 'dashboards/billing'
            }
          ]
        },
        {
          name: 'Patients',
          icon: routeIconMap['patients'].icon,
          path: 'patients'
        },
        {
          name: 'Appointments',
          icon: routeIconMap['appointments'].icon,
          children: [
            {
              name: 'Appointments',
              icon: routeIconMap['appointments'].icon,
              path: 'scheduling/appointments'
            },
            {
              name: 'Waiting List',
              icon: routeIconMap['waitinglist'].icon,
              path: 'scheduling/waitinglist'
            },
            {
              name: 'Booked Procedures',
              icon: routeIconMap['bookedprocedures'].icon,
              path: 'scheduling/bookedprocedures'
            },
            {
              name: 'Waiting Room',
              icon: routeIconMap['waitingroom'].icon,
              path: 'scheduling/waitingroom'
            }
          ]
        },
        {
          name: 'Billing',
          icon: routeIconMap['billing'].icon,
          children: [
            {
              name: 'Invoices',
              icon: routeIconMap['invoices'].icon,
              path: 'billing/invoices'
            },
            {
              name: 'Eclipse Claims',
              icon: routeIconMap['eclipse-claims'].icon,
              path: 'billing/eclipse-claims'
            },
            {
              name: 'Billing Templates',
              icon: routeIconMap['templates'].icon,
              path: 'billing/templates'
            },
            {
              name: 'MBS Items',
              icon: routeIconMap['mbs-items'].icon,
              path: 'billing/mbs-items'
            }
          ]
        },
        {
          name: 'Accounting',
          icon: routeIconMap['accounts'].icon,
          children: [
            {
              name: 'Chart of Accounts',
              icon: routeIconMap['chart-of-accounts'].icon,
              path: 'accounts/chart-of-accounts'
            },
            {
              name: 'Journal Entries',
              icon: routeIconMap['journal-entries'].icon,
              path: 'accounts/journal-entries'
            }
          ]
        },
        {
          name: 'Documents',
          icon: routeIconMap['documents'].icon,
          path: 'documents'
        },
        {
          name: 'Email',
          icon: routeIconMap['email'].icon,
          children: [
            {
              name: 'Email List',
              icon: routeIconMap['email'].icon,
              path: 'email'
            },
            {
              name: 'Compose Email',
              icon: routeIconMap['compose'].icon,
              path: 'email/compose'
            },
            {
              name: 'Email Templates',
              icon: routeIconMap['templates'].icon,
              path: 'email/templates'
            },
            {
              name: 'Signatures',
              icon: routeIconMap['signatures'].icon,
              path: 'signatures'
            }
          ]
        },
        {
          name: 'Address Book',
          icon: routeIconMap['addressbook'].icon,
          path: 'addressbook'
        },
        {
          name: 'Dictations',
          icon: routeIconMap['dictations'].icon,
          path: 'letters/dictations'
        },
        {
          name: 'AI',
          icon: routeIconMap['ai'].icon,
          children: [
            {
              name: 'AI Integration Hub',
              icon: routeIconMap['integration'].icon,
              path: 'ai/integration'
            },
            {
              name: 'AI Clinical Assistant',
              icon: routeIconMap['assistant'].icon,
              path: 'ai/assistant'
            }
          ]
        },
        {
          name: 'Pain Workup Sheet',
          icon: routeIconMap['painworkupsheet'].icon,
          path: 'clinicalassessment/painworkupsheet'
        },
        {
          name: 'Standard Workup Sheet',
          icon: routeIconMap['standardworkupsheet'].icon,
          path: 'clinicalassessment/standardworkupsheet'
        },
        {
          name: 'Create Body Pain Point Chart',
          icon: routeIconMap['createbodypainpointchart'].icon,
          path: 'bodydiagrams/createbodypainpointchart'
        },
        {
          name: 'Publications',
          icon: routeIconMap['publications'].icon,
          children: [
            {
              name: 'Publication Search',
              icon: routeIconMap['publicationsearch'].icon,
              path: 'publications/search'
            }
          ]
        },
        {
          name: 'Tools',
          icon: routeIconMap['tools'].icon,
          children: [
            {
              name: 'Requests Tracker',
              icon: routeIconMap['requeststracker'].icon,
              path: 'tools/requeststracker'
            },
            {
              name: 'Opioid Calculator',
              icon: routeIconMap['opioidcalculator'].icon,
              path: 'tools/opioidcalculator'
            }
          ]
        },

      ]
    },
    {
      title: 'Settings',
      links: [
        {
          name: 'Dictation',
          icon: routeIconMap['letters'].icon,
          children: [
            {
              name: 'Dictation Types',
              icon: routeIconMap['dictation-types'].icon,
              path: 'letters/dictation-types'
            }
          ],
        },
        {
          name: 'Security',
          icon: routeIconMap['security'].icon,
          children: [
            {
              name: 'Users',
              icon: routeIconMap['users'].icon,
              path: 'security/users'
            },
            {
              name: 'Groups',
              icon: routeIconMap['groups'].icon,
              path: 'security/groups'
            }
          ],
        },
        ...(isAdminUser ? [{
          name: 'Clients',
          icon: routeIconMap['clients'].icon,
          children: [
            {
              name: 'Client Settings',
              icon: routeIconMap['clientsettings'].icon,
              path: 'admin/clients'
            }
          ]
        }] : []),
        {
          name: 'Facilities',
          icon: routeIconMap['facilities'].icon,
          children: [
            {
              name: 'Facilities',
              icon: routeIconMap['facilities'].icon,
              path: 'facilities/facilities'
            },
            {
              name: 'Facility Types',
              icon: routeIconMap['facilitytypes'].icon,
              path: 'facilities/facilitytypes'
            },
            {
              name: 'Facility Room Types',
              icon: routeIconMap['facilityroomtypes'].icon,
              path: 'facilities/facilityroomtypes'
            },
            {
              name: 'Facility Rooms',
              icon: routeIconMap['facilityrooms'].icon,
              path: 'facilities/facilityrooms'
            }
          ],
        },
        { name: 'Provider Settings',
          icon: routeIconMap['providers'].icon,
          children: [
            {
              name: 'Providers',
              icon: routeIconMap['providers'].icon,
              path: 'facilities/providers'
            },
            {
              name: 'Provider Types',
              icon: routeIconMap['providertypes'].icon,
              path: 'providers/providertypes'
            },
            {
              name: 'Provider Sessions',
              icon: routeIconMap['sessions'].icon,
              path: 'facilities/sessions/sessionrosters'
            }
          ],
        },
        {
          name: 'Procedure Settings',
          icon: routeIconMap['procedures'].icon,
          children: [
            {
              name: 'Procedure Types',
              icon: routeIconMap['proceduretypes'].icon,
              path: 'procedures/proceduretypes'
            },
            {
              name: 'Appointment Types',
              icon: routeIconMap['appointmenttypes'].icon,
              path: 'scheduling/appointmenttypes'
            },
            {
              name: 'Procedure Type Categories',
              icon: routeIconMap['proceduretypecategories'].icon,
              path: 'procedures/proceduretypecategories'
            }
          ],
        },
        {
          name: 'Session Rostering',
          icon: routeIconMap['sessionrosters'].icon,
          path: 'facilities/sessions/sessionrosters'
        },
        {
          name: 'Session Types',
          icon: routeIconMap['sessiontypes'].icon,
          path: 'sessions/sessiontypes'
        },
        {
          name: 'Documents',
          icon: routeIconMap['documenttypes'].icon,
          children: [
            {
              name: 'Document Types',
              icon: routeIconMap['documenttypes'].icon,
              path: 'documents/documenttypes'
            },
            {
              name: 'Document Categories',
              icon: routeIconMap['documentcategories'].icon,
              path: 'documents/documentcategories'
            },
            {
              name: 'Document Templates',
              icon: routeIconMap['templates'].icon,
              path: 'documents/templates'
            }
          ]
        }
      ]
    },
  ];

  const renderMenuItem = (item: MenuItem) => {
    if (sidebarState === 'collapsed') {
      // Collapsed state: flatten all items with paths to show as individual icons
      if (item.children) {
        // For parent items with children, render each child individually
        return (
          <React.Fragment key={item.name}>
            {item.children.map((child) => (
              <li key={child.name}>
                <TooltipComponent content={child.name} position="RightCenter">
                  <NavLink to={`/${child.path || child.name}`} onClick={handleCloseSideBar} className="flex justify-center">
                    <span className="text-xl">{child.icon}</span>
                  </NavLink>
                </TooltipComponent>
              </li>
            ))}
          </React.Fragment>
        );
      }
      // Single item without children
      if (item.path) {
        return (
          <li key={item.name}>
            <TooltipComponent content={item.name} position="RightCenter">
              <NavLink to={`/${item.path}`} onClick={handleCloseSideBar} className="flex justify-center">
                <span className="text-xl">{item.icon}</span>
              </NavLink>
            </TooltipComponent>
          </li>
        );
      }
      return null;
    }

    // Full state: show icons and text
    if (item.children) {
      return (
        <li key={item.name}>
          <details open>
            <summary>
              <span className="flex items-center gap-3">
                <span className="text-lg">{item.icon}</span>
                {item.name}
              </span>
            </summary>
            <ul>
              {item.children.map((child) => renderMenuItem(child))}
            </ul>
          </details>
        </li>
      );
    }

    return (
      <li key={item.name}>
        <NavLink to={`/${item.path || item.name}`} onClick={handleCloseSideBar}>
          <span className="flex items-center gap-3">
            <span className="text-lg">{item.icon}</span>
            {item.name}
          </span>
        </NavLink>
      </li>
    );
  };

  return (
    <div className={`h-screen flex flex-col bg-base-100 overflow-hidden transition-all duration-300 ${
      sidebarState === 'hidden' ? 'w-0' : sidebarState === 'collapsed' ? 'w-16' : 'w-64'
    }`}>
      {sidebarState !== 'hidden' && (
        <>
          {/* Logo section */}
          <div className="pb-3">
            <div className="flex justify-between items-center p-3">
              <Link
                to="/"
                onClick={handleCloseSideBar}
                className="flex w-full"
              >
                {sidebarState === 'full' ? (
                  <div className="w-full flex items-center justify-center">
                    <TMLogo className="h-28 w-auto tm-logo-tone" />
                  </div>
                ) : (
                  <TooltipComponent content="Templar Edge" position="RightCenter">
                    <div className="w-full flex items-center justify-center">
                      <TMLogoIcon className="h-10 w-10 tm-logo-tone" />
                    </div>
                  </TooltipComponent>
                )}
              </Link>
              {screenSize && screenSize <= 900 && (
                <TooltipComponent content="Close" position="BottomCenter">
                  <button
                    type="button"
                    onClick={() => setSidebarState('hidden')}
                    className="text-xl text-base-content rounded-full p-3 hover:bg-base-200"
                  >
                    <MdOutlineCancel />
                  </button>
                </TooltipComponent>
              )}
            </div>
          </div>

          {/* Menu items */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {multiLevelLinks.map((section) => (
              <div key={section.title} className="mb-4">
                {sidebarState === 'full' && section.title && (
                  <p className="px-4 py-1 text-xs font-semibold uppercase text-base-content/50 tracking-widest">
                    {section.title}
                  </p>
                )}
                <ul className="menu menu-sm w-full gap-0.5">
                  {section.links.map((item) => renderMenuItem(item))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;